import React, { useState, useEffect } from 'react';
import { Code, AlertCircle, Check } from 'lucide-react';

interface JSONEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
}

export default function JSONEditor({
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder = '{\n  "key": "value"\n}',
  helpText
}: JSONEditorProps) {
  const [isValid, setIsValid] = useState(true);
  const [parseError, setParseError] = useState('');

  useEffect(() => {
    if (value) {
      try {
        JSON.parse(value);
        setIsValid(true);
        setParseError('');
      } catch (e) {
        setIsValid(false);
        setParseError((e as Error).message);
      }
    } else {
      setIsValid(true);
      setParseError('');
    }
  }, [value]);

  const formatJSON = () => {
    try {
      const parsed = JSON.parse(value);
      onChange(JSON.stringify(parsed, null, 2));
    } catch {
      // Cannot format invalid JSON
    }
  };

  // Sample FAQ template
  const insertTemplate = () => {
    const template = [
      {
        question: "What is your return policy?",
        answer: "We offer a 30-day return policy for all items.",
        order: 1,
        tags: ["returns", "policy"]
      },
      {
        question: "How long does shipping take?",
        answer: "Standard shipping takes 5-7 business days.",
        order: 2,
        tags: ["shipping", "delivery"]
      }
    ];
    onChange(JSON.stringify(template, null, 2));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex items-center gap-2">
          {value && (
            <button
              type="button"
              onClick={formatJSON}
              disabled={!isValid}
              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400"
            >
              Format
            </button>
          )}
          {label.includes('FAQ') && (
            <button
              type="button"
              onClick={insertTemplate}
              className="text-xs text-purple-600 hover:text-purple-800"
            >
              Insert Template
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <div className="absolute top-2 right-2 pointer-events-none">
          {value && (
            isValid ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-500" />
            )
          )}
        </div>

        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={12}
          className={`w-full px-3 py-2 font-mono text-sm border rounded-md focus:outline-none focus:ring-2  ${
            error || !isValid
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
          }`}
          style={{ tabSize: 2 }}
        />
      </div>

      {!isValid && parseError && (
        <p className="mt-1 text-sm text-red-600">
          <Code className="inline w-3 h-3 mr-1" />
          JSON Error: {parseError}
        </p>
      )}

      {helpText && !error && isValid && (
        <p className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}